import type { ReactNode } from 'react';
import { useCallback } from 'react';

import { PullstateCore } from '../store';
import type { Optional } from '../types';
import { ClientOutdatedError } from './errors';

export type AlertId = string | number;

export interface IRasAlertAddData {
  actions?: {
    text: string;
    href?: string;
    onClick?: React.DOMAttributes<HTMLElement>['onClick'];
  }[];
  duration?: number;
  icon?: ReactNode | string | null | undefined;
  href?: string;
  id: AlertId;
  message: string;
  permanent?: boolean;
  submessage?: string;
  pre?: string;
  status?: 'info' | 'success' | 'warning' | 'error' | undefined;
  to?: 'toast' | 'main' | 'desktop' | 'aside';
  uFirst?: string;
  uLast?: string;
  uId?: API.Id;
  uRole?: string;
  _timeout?: NodeJS.Timeout;
}

/** Omits JSX elements so that it can be serialized properly */
export type SimpleRasAlertAddData = {
  [P in keyof IRasAlertAddData]: Exclude<IRasAlertAddData[P], JSX.Element>;
};

export interface CustomEventMap {
  rasalertadd: CustomEvent<IRasAlertAddData>;
  rasalertremove: CustomEvent<AlertId>;
}

interface RasAlertEventTarget extends EventTarget {
  addEventListener<K extends keyof CustomEventMap>(
    type: K,
    listener:
      | ((ev: CustomEventMap[K]) => void)
      | Record<'handleEvent', (evt: CustomEventMap[K]) => void>
      | null,
    options?: boolean | AddEventListenerOptions,
  ): void;
  dispatchEvent<K extends keyof CustomEventMap>(ev: CustomEventMap[K]): boolean;
  removeEventListener<K extends keyof CustomEventMap>(
    type: K,
    listener:
      | ((ev: CustomEventMap[K]) => void)
      | Record<'handleEvent', (evt: CustomEventMap[K]) => void>
      | null,
    options?: boolean | EventListenerOptions,
  ): void;
}

export const eventTarget: RasAlertEventTarget = new EventTarget();

export function dispatchOutdatedAlert(data?: Omit<IRasAlertAddData, 'id'>) {
  eventTarget.dispatchEvent(
    new CustomEvent('rasalertadd', {
      detail: {
        ...data,
        message: data?.message || ClientOutdatedError.DEFAULT_MESSAGE,
        id: 'swneedsupdate',
        status: 'success',
        permanent: true,
        actions: [{ text: 'Reload now', onClick: () => location.reload() }],
      },
    }),
  );
}

export function dispatchShowAlert(data: Optional<IRasAlertAddData, 'id'>) {
  const id = getId(data);

  eventTarget.dispatchEvent(
    new CustomEvent('rasalertadd', {
      detail: { id, ...data },
    }),
  );
}

export function dispatchRemoveAlert(id: AlertId) {
  eventTarget.dispatchEvent(
    new CustomEvent('rasalertremove', {
      detail: id,
    }),
  );
}

function getId(data: Optional<IRasAlertAddData, 'id'>) {
  return data.id || `${Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}`;
}

export function useAlerts() {
  const { AlertsStore } = PullstateCore.useStores();

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const show = useCallback((data: Optional<SimpleRasAlertAddData, 'id'>) => {
    const id = getId(data);

    AlertsStore.update((store) => {
      const to = data.to || 'toast';

      if (to === 'desktop') {
        // @todo desktop notifications
      } else {
        store[to][id] = {
          id,
          ...data,
        };
      }
    });

    return id;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const remove = useCallback(
    (
      id: AlertId,
      to?:
        | undefined
        | Exclude<IRasAlertAddData['to'], undefined>
        | Exclude<IRasAlertAddData['to'], undefined>[],
    ) => {
      AlertsStore.update((store) => {
        const tos: Exclude<IRasAlertAddData['to'], undefined>[] =
          to && Array.isArray(to) ? to : !to ? ['toast', 'main', 'desktop', 'aside'] : [to];

        for (const key of tos) {
          if (key === 'desktop') {
            // @todo desktop notifications
          } else {
            if (store[key][id]) {
              delete store[key][id];
            }
          }
        }
      });
    },
    [],
  );

  return {
    show,
    remove,
    store: AlertsStore,
  };
}
